/**
 * 혜택 목록의 **쿠폰코드 적재 현황** 표시 규칙(2026-09-10 사용자 지시).
 *
 * 서버(`BenefitListResponse`)가 주는 값은 세 개다 — `type`·`useCodePool`·`codePool`.
 * 화면이 다섯 가지 상태를 구분해야 하는데 분기를 JSX 안에 두면 검증할 수 없어서 여기로 뺐다.
 *
 * ## 왜 이 정보를 목록에 붙이나
 *
 * 코드형 혜택은 고객사가 쿠폰 코드를 미리 적재해 두고 하나씩 나눠 준다. 다 나가면 발급이
 * 멈추는데(백엔드 `BenefitCodePoolExhaustedException`) 종전 목록에는 적재량도 소진량도
 * 없어서 **소진된 뒤 고객 문의로** 알게 됐다.
 *
 * 🔑 경고 기준(10%)은 백엔드 `BenefitCodeIssuer.LOW_STOCK_RATIO` 와 **같은 값**이다.
 *    서버 로그가 `[CODE_POOL_LOW]` 를 찍기 시작하는 시점과 화면이 노랗게 변하는 시점을
 *    일치시키기 위한 것이다. 한쪽만 바꾸면 "로그는 경고인데 화면은 정상" 이 된다.
 */

/** 이 비율 이하로 남으면 '곧 소진'. 백엔드 `BenefitCodeIssuer.LOW_STOCK_RATIO` 와 동일. */
export const LOW_STOCK_RATIO = 0.10

/**
 * ## 🔴 '적재 없이 발급' 과 '적재 필요' 는 다른 상태다 (2026-09-10 2차 질문)
 *
 * 종전 문구 '코드 적재 안 함' / '코드 미적재' 가 거의 같은 말로 읽혀서 이름을 바꿨다.
 * 실제로는 **정상 상태**와 **발급 불가 상태**로 정반대다:
 *
 * | tone | 뜻 | 담당자가 할 일 |
 * |------|-----|----------------|
 * | `none`(적재 없이 발급) | `useCodePool = false`. 모든 고객에게 **같은 고정 코드**를 주거나 서버가 **12자리를 자동 생성**한다. 나눠 줄 코드 목록이라는 개념 자체가 없다 | **없다**(정상) |
 * | `unloaded`(적재 필요) | `useCodePool = true` 인데 `benefit_code_pool` 행이 **0건**. 발급을 시도하면 `BenefitCodePoolExhaustedException` 이 나고 고객은 쿠폰을 못 받는다 | **코드 파일을 올려야 한다** |
 *
 * 근거 = `BenefitCodeIssuer.issueCode()` 의 분기
 * (`if (benefit.useCodePool) issueFromPool(benefit) else benefit.code ?: issueRandom(...)`).
 *
 * @returns `null` = 코드형이 아니라 표시할 것이 없다.
 *   그 외에는 `{tone, title, detail, loaded, used, remaining}`.
 *   `tone` = `none` | `ok` | `low` | `empty` | `unloaded`
 */
export const describeCodePool = (benefit) => {
    if (benefit?.type !== 'CODE') return null

    // 고정코드·랜덤생성 코드형. 적재라는 개념이 없으므로 숫자를 보여주면 오해가 된다.
    if (!benefit.useCodePool) {
        return {
            tone: 'none',
            title: '적재 없이 발급',
            detail: '고정 코드 / 자동 생성',
            loaded: null, used: null, remaining: null,
        }
    }

    const pool = benefit.codePool
    // 서버는 pool 행이 0건이면 `codePool` 자체를 주지 않는다(= 아직 한 건도 올리지 않았다).
    if (!pool || !pool.loaded) {
        return {
            tone: 'unloaded',
            title: '적재 필요',
            detail: '코드 0개 · 발급 불가',
            loaded: 0, used: 0, remaining: 0,
        }
    }

    const { loaded, used, remaining } = pool
    const tone = remaining <= 0 ? 'empty'
        : remaining <= loaded * LOW_STOCK_RATIO ? 'low'
            : 'ok'

    /*
      🔑 **숫자만 쓴다**(2026-09-10 2차). 칸 폭이 130px 이라 '개 사용' 같은 꼬리말을 붙이면
         줄이 넘어가고, `사용 / 적재` 라는 뜻은 아래 잔량 줄과 색으로 이미 전달된다.
         '남은 코드' → '잔량' 으로 줄인 것도 같은 이유다.
    */
    return {
        tone,
        title: `${used.toLocaleString()} / ${loaded.toLocaleString()}`,
        detail: remaining <= 0
            ? '소진 · 추가 적재 필요'
            : `잔량 ${remaining.toLocaleString()}개`,
        loaded, used, remaining,
    }
}

/** 사용률(0~100). 적재량이 0이면 0. 진행 막대 폭에 쓴다. */
export const usedPercent = (status) => {
    if (!status?.loaded) return 0
    return Math.min(100, Math.round((status.used / status.loaded) * 100))
}
